import { useEffect, useState } from "react";
import type { CmmdTaskStatus } from "@cmmd-forge/contracts";

import type { CommandCenterView } from "./command-center/FilterBar";
import {
  readStoredWorkspaceFilter,
  subscribeStoredWorkspaceFilter,
  writeStoredWorkspaceFilter,
} from "./command-center/workspaceFilterStorage";
import {
  COMMAND_CENTER_ASSIGNEE_FILTER_KEY,
  COMMAND_CENTER_CAMPAIGN_FILTER_KEY,
  COMMAND_CENTER_PROJECT_FILTER_KEY,
  COMMAND_CENTER_SEARCH_QUERY_KEY,
  COMMAND_CENTER_STATUS_FILTER_KEY,
  COMMAND_CENTER_TEAM_FILTER_KEY,
  COMMAND_CENTER_VIEW_KEY,
  readStoredAssigneeFilter,
  readStoredCampaignFilter,
  readStoredProjectFilter,
  readStoredSearchQuery,
  readStoredStatusFilter,
  readStoredTeamFilter,
  readStoredView,
} from "./NoActiveThreadState.logic";
import { writeStoredValue } from "../lib/storage";

export function useCommandCenterPersistentState() {
  const [view, setView] = useState<CommandCenterView>(() => readStoredView());
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(() =>
    readStoredWorkspaceFilter(),
  );
  const [readyOnly, setReadyOnly] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<CmmdTaskStatus | "all">(() =>
    readStoredStatusFilter(),
  );
  const [teamFilter, setTeamFilter] = useState<string>(() => readStoredTeamFilter());
  const [campaignFilter, setCampaignFilter] = useState<string>(() => readStoredCampaignFilter());
  const [projectFilter, setProjectFilter] = useState<string>(() => readStoredProjectFilter());
  const [assigneeFilter, setAssigneeFilter] = useState<string>(() => readStoredAssigneeFilter());
  const [searchQuery, setSearchQuery] = useState<string>(() => readStoredSearchQuery());

  usePersistedCommandCenterState({
    view,
    activeWorkspaceId,
    statusFilter,
    teamFilter,
    campaignFilter,
    projectFilter,
    assigneeFilter,
    searchQuery,
  });
  useEffect(
    () =>
      subscribeStoredWorkspaceFilter((nextWorkspaceId) => {
        setActiveWorkspaceId(nextWorkspaceId);
      }),
    [],
  );

  return {
    view,
    setView,
    activeWorkspaceId,
    setActiveWorkspaceId,
    readyOnly,
    setReadyOnly,
    statusFilter,
    setStatusFilter,
    teamFilter,
    setTeamFilter,
    campaignFilter,
    setCampaignFilter,
    projectFilter,
    setProjectFilter,
    assigneeFilter,
    setAssigneeFilter,
    searchQuery,
    setSearchQuery,
  };
}

function usePersistedCommandCenterState(input: {
  readonly view: CommandCenterView;
  readonly activeWorkspaceId: string | null;
  readonly statusFilter: CmmdTaskStatus | "all";
  readonly teamFilter: string;
  readonly campaignFilter: string;
  readonly projectFilter: string;
  readonly assigneeFilter: string;
  readonly searchQuery: string;
}) {
  useEffect(() => writeBrowserStorage(COMMAND_CENTER_VIEW_KEY, input.view), [input.view]);
  useEffect(
    () => writeBrowserStorage(COMMAND_CENTER_STATUS_FILTER_KEY, input.statusFilter),
    [input.statusFilter],
  );
  useEffect(
    () => writeBrowserStorage(COMMAND_CENTER_TEAM_FILTER_KEY, input.teamFilter),
    [input.teamFilter],
  );
  useEffect(
    () => writeBrowserStorage(COMMAND_CENTER_CAMPAIGN_FILTER_KEY, input.campaignFilter),
    [input.campaignFilter],
  );
  useEffect(
    () => writeBrowserStorage(COMMAND_CENTER_PROJECT_FILTER_KEY, input.projectFilter),
    [input.projectFilter],
  );
  useEffect(
    () => writeBrowserStorage(COMMAND_CENTER_ASSIGNEE_FILTER_KEY, input.assigneeFilter),
    [input.assigneeFilter],
  );
  useEffect(
    () => writeBrowserStorage(COMMAND_CENTER_SEARCH_QUERY_KEY, input.searchQuery),
    [input.searchQuery],
  );
  useEffect(() => writeStoredWorkspaceFilter(input.activeWorkspaceId), [input.activeWorkspaceId]);
}

function writeBrowserStorage(key: string, value: string): void {
  writeStoredValue(key, value);
}
